// The words of the INBOX (docs/design/inbox.md §6; the word file
// docs/review/inbox-words-2026-10-09.md). The server sends facts: the
// event id, the typed figures and the rows of a table (inbox.md §3). This
// module writes them in the reader's language: the type word, line 1,
// the figure cells of line 2, the sentences of the open message, its
// table and its place. `rows.ts` draws a row from these parts and
// `open.ts` the open message.
//
// One type has one shape (build plan §1 rule 1): every event returns the
// same cells in the same order whatever its figures, so a long and a
// short name, a large and a small figure, a single message and a fold
// stand at the same x. A cell keeps its width when it is empty.
//
// Every word comes from `[ui.inbox]` of data/locale/<tag>/ui.toml; every
// stamp goes through `locale/stamp.ts` and every figure through
// `locale/group.ts`. A name from the wire is escaped here, once.

import { toInt } from '../../util/num';
import type { InboxMember, InboxMsg, InboxRow, InboxTable } from '../../gen/proto/aquaphobia';
import { card as CS, inbox as S } from '../../gen/strings';
import { groupThousands } from '../../locale/group';
import { isoDate, isoNear, isoTime } from '../../locale/stamp';
import type { GameState } from '../../state';
import { cellCenter, factionColor, factionName, hex } from '../../world';
import { cellPos } from '../chartpos';
import { roomName } from '../clubroom';
import { esc } from '../esc';
import { benefitText, practiceFig } from '../format';
import { diveSpec, moduleGradeWord } from '../overlay/markup';
import { resIcon, withResIcons, type ResIcon } from '../resicon';
import { gradeName, nameOf as achievementName } from '../unlock';
import { inboxIcon, type InboxIcon } from './icons';

// ---------------------------------------------------------------------------
// The facts of one row
// ---------------------------------------------------------------------------

/** What one row reads: a message, a fold or one member of a fold. */
export interface Facts {
  /** The event id; '' for a message filed before the build. */
  ev: string;
  p: Record<string, string>;
  tables: readonly InboxTable[];
  cell: number;
  unix: number;
  /** The stamp of the first member of a fold; `unix` for a single row. */
  first: number;
  /** The members of a fold, 2 or more; 0 for a single message. */
  count: number;
  /** The members of a fold, oldest first; none for a single message. */
  members: readonly InboxMember[];
  s: GameState;
  /** The wall clock in Unix seconds, for the stamps and the countdown. */
  now: number;
  /** The stored words of a message filed before the build. */
  title: string;
  body: string;
}

/** The wall clock in Unix seconds. */
export function nowSec(): number {
  return Math.floor(Date.now() / 1000);
}

/** The facts of a message or of a fold row. */
export function factsOf(m: InboxMsg, s: GameState, now = nowSec()): Facts {
  return {
    ev: m.event, p: m.params, tables: m.rows, cell: m.cell, unix: Number(m.unix),
    first: m.count >= 2 && Number(m.firstUnix) > 0 ? Number(m.firstUnix) : Number(m.unix),
    count: m.count >= 2 ? m.count : 0, members: m.count >= 2 ? m.members : [],
    s, now, title: m.title, body: m.body,
  };
}

/** The facts of one member of a fold: the event of its fold, its own
 *  figures, tables, cell and stamp. */
export function memberFacts(m: InboxMsg, mem: InboxMember, s: GameState, now = nowSec()): Facts {
  return {
    ev: m.event, p: mem.params, tables: mem.rows, cell: mem.cell, unix: Number(mem.unix),
    first: Number(mem.unix), count: 0, members: [], s, now, title: '', body: '',
  };
}

const str = (f: Facts, k: string): string => f.p[k] ?? '';
const has = (f: Facts, k: string): boolean => str(f, k) !== '';
const int = (f: Facts, k: string): number => toInt(str(f, k));

/** A figure with its thousands grouped (R687). */
export function fig(n: number): string {
  return groupThousands(n);
}

/** The table of one name, or none. */
function table(f: Facts, name: string): readonly InboxRow[] {
  return f.tables.find((t) => t.name === name)?.rows ?? [];
}

const cellInt = (r: InboxRow, k: string): number => toInt(r.cells[k] ?? '');

// ---------------------------------------------------------------------------
// The forms of a value (word file rule 2)
// ---------------------------------------------------------------------------

/** A hull class by its `ShipClass` number, named from the hello catalog. */
function className(s: GameState, v: string): string {
  return s.ship(Number(v))?.name ?? '';
}

/** A torpedo by its `TorpedoKind` number, named from the hello catalog. */
function torpedoName(s: GameState, v: string): string {
  return s.torpedo(Number(v))?.name ?? '';
}

/** The faction square of a faction number (build plan §1: no colour
 *  alone, so the name stands beside it). */
export function square(faction: number): string {
  return `<i class="ib-sq" style="background:${hex(factionColor(faction))}" aria-hidden="true"></i>`;
}

/** A faction in a sentence: its square, then its name. */
function factionHtml(faction: number): string {
  return `${square(faction)}${esc(factionName(faction))}`;
}

/** The label of a cell where the server has no place name (rule 7): its
 *  chart position (R2742). Cell 0 names no place. */
export function cellLabel(cell: number): string {
  if (cell <= 0) return '';
  return cellPos(cell);
}

/**
 * The name of the place the server named for a message (inbox.md §3.1,
 * R3132), in the reader's language, or '' when it named none. The server
 * sends the facts `place`, `place_kind` and `place_id`, and this writes
 * the words: the reader's own hold reads "Your hold", another hold the
 * commander's hold, a seamount the name of the reader's language, and an
 * object or a named trench its name. A `place` with no kind that is a
 * figure is the cell a message filed before R3132 carries, and names no
 * place.
 */
export function placeText(p: Readonly<Record<string, string>>, s: GameState): string {
  const name = p.place ?? '';
  if (!name) return '';
  const id = toInt(p.place_id ?? '');
  switch (p.place_kind ?? '') {
    case 'hold': {
      const own = s.playerId > 0 && id > 0 && s.sites.get(id)?.ownerPlayer === s.playerId;
      return own ? S.your_hold : S.place_hold({ commander: name });
    }
    case 'seamount':
      return s.seamounts.byBody(id)?.name || name;
    case '':
      return /^\d+$/.test(name) ? '' : name;
    default:
      return name;
  }
}

/** The place of a message: the name the server sent, else the label of
 *  its cell (rule 7). */
function placeName(f: Facts): string {
  return placeText(f.p, f.s) || cellLabel(f.cell);
}

/** The short stamp of a row: the time today, the month and the day on
 *  another day (the ISO form of `locale/stamp.ts` less its year). */
export function shortStamp(unix: number, now: number): string {
  if (unix <= 0) return '';
  if (isoDate(unix) === isoDate(now)) return isoTime(unix);
  return isoDate(unix).slice(5);
}

/** A time key in a sentence: the time today, the date and the time on
 *  another day. */
function timeOf(f: Facts, k: string): string {
  return isoNear(int(f, k), f.now);
}

/** A span of wall seconds as `2h 40m` or `40m`. It counts minutes, so a
 *  countdown changes the markup once a minute (audit P11). */
export function span(sec: number): string {
  const min = Math.max(0, Math.ceil(sec / 60));
  if (min < 60) return S.span_m({ m: min });
  return S.span_hm({ h: Math.floor(min / 60), m: min % 60 });
}

/** The time range of a fold or a window, `08:12–09:40`. */
function range(a: number, b: number, now: number): string {
  return S.range({ first: shortStamp(a, now), last: shortStamp(b, now) });
}

// ---------------------------------------------------------------------------
// The figure cells of line 2
// ---------------------------------------------------------------------------

/** The ink of a cell: the role of its figure (§2.4 of the interface). */
export type Ink = '' | 'good' | 'bad' | 'dim';

/** A mark beside a figure: a drawn INBOX icon or a resource icon. */
export interface Mark {
  icon: InboxIcon | ResIcon;
  /** A resource icon of `ui/resicon.ts`, not an INBOX icon. */
  res?: boolean;
}

/** The width of a cell in characters, from the word file; 0 takes the
 *  rest of the line. The sheet holds one rule for each width. */
export type Width = 0 | 2 | 5 | 6 | 7 | 8 | 9 | 10 | 12 | 13 | 14;

/**
 * One cell of the figure strip.
 *
 * - `num`: a figure, right in its width. `text` is the whole word with the
 *   figure in it ("12 hits", "−420 cells"); the figure stands in the width
 *   and the rest of the word after it in the small step. A zero reads as a
 *   faint dash, and its word stays faint.
 * - `txt`: a word in its width, cut with an ellipsis.
 * - `fac`: a faction square and a name.
 * - `flag`: a mark that is drawn hollow when it is absent.
 */
export type Cell =
  | { t: 'num'; w: Width; n: number; text: string; mark?: Mark; ink?: Ink }
  | { t: 'txt'; w: Width; text: string; ink?: Ink; right?: boolean }
  | { t: 'fac'; w: Width; faction: number; name: string }
  | { t: 'flag'; on: boolean; mark: Mark; ink?: Ink };

const num = (w: Width, n: number, text: string, more: { mark?: Mark; ink?: Ink } = {}): Cell =>
  ({ t: 'num', w, n, text, ...more });
const txt = (w: Width, text: string, ink: Ink = '', right = false): Cell => ({ t: 'txt', w, text, ink, right });
const fac = (w: Width, faction: number, name: string): Cell => ({ t: 'fac', w, faction, name });

/** A faction cell from a faction key and a name key: the faction name
 *  when the name key is empty. */
function facCell(f: Facts, w: Width, facKey: string, nameKey = ''): Cell {
  const faction = int(f, facKey);
  const name = nameKey ? str(f, nameKey) : '';
  if (faction <= 0 && name === '') return txt(w, '');
  return fac(w, faction, name || (faction > 0 ? factionName(faction) : ''));
}

// ---------------------------------------------------------------------------
// The tables and the places of the open message
// ---------------------------------------------------------------------------

/** One table of an open message: its title, its column heads and its
 *  rows as markup. A column that holds figures reads to the right. */
export interface OpenTable {
  title?: string;
  heads: readonly string[];
  num: readonly boolean[];
  rows: string[][];
}

/** The place line of an open message: the name and the cell of GO TO. */
export interface Place {
  name: string;
  cell: number;
}

/** The faint dash of an empty cell or a zero. */
export const DASH = '<span class="ib-z">–</span>';

/** A figure cell of a table: a faint dash for a zero. */
function tableFig(n: number): string {
  return n === 0 ? DASH : fig(n);
}

/** The fleet name of a table row. */
const fleetCell = (r: InboxRow): string => esc(r.cells.fleet ?? '');

/** The hull class of a table row, named from the hello catalog. */
const classCell = (f: Facts, r: InboxRow): string => esc(className(f.s, r.cells.class ?? ''));

/** An owner in a table: the faction square and the commander. */
function ownerCell(r: InboxRow): string {
  const faction = cellInt(r, 'faction');
  const owner = r.cells.owner ?? '';
  return `${faction > 0 ? square(faction) : ''}${esc(owner || (faction > 0 ? factionName(faction) : ''))}`;
}

/** The HULL column of a torpedo report: the percent after the blast, or
 *  SUNK in the bad ink at 0. */
function hullCell(r: InboxRow): string {
  const pct = cellInt(r, 'hull_pct');
  return pct <= 0 ? `<span class="ib-bad">${esc(S.hull_sunk)}</span>` : esc(S.hull_pct({ n: pct }));
}

/** What a damage row names: its fleet, or the kind of damage no hull dealt. */
function sourceCell(r: InboxRow): string {
  const fleet = r.cells.fleet ?? '';
  if (fleet) return esc(fleet);
  const kind = cellInt(r, 'kind');
  return `<span class="ib-dim">${esc(S.damage_kinds[kind - 1] ?? '')}</span>`;
}

/** The cells of a lost hull as a small hex map, in the bad ink: the shape
 *  of the water that went, with no words (word file §3.3). */
function hexMap(cells: readonly number[]): string {
  if (cells.length === 0) return '';
  const pts = cells.map((c) => cellCenter(c));
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x0 = Math.min(...xs);
  const y0 = Math.min(...ys);
  const dx = Math.max(...xs) - x0;
  const dy = Math.max(...ys) - y0;
  const span = Math.max(dx, dy, 1);
  // The map fits a box of 120 units; one hex keeps a radius of 7.
  const k = Math.min(1, 104 / span);
  const r = 7;
  let out = '';
  for (const [x, y] of pts) {
    const cx = (x - x0) * k + r + 1;
    const cy = (y - y0) * k + r + 1;
    let d = '';
    for (let i = 0; i < 6; i++) {
      const a = ((60 * i - 90) * Math.PI) / 180;
      d += `${i ? 'L' : 'M'}${(cx + Math.cos(a) * (r - 0.6)).toFixed(1)} ${(cy + Math.sin(a) * (r - 0.6)).toFixed(1)}`;
    }
    out += `<path d="${d}Z"/>`;
  }
  const w = Math.ceil(dx * k + 2 * r + 2);
  const h = Math.ceil(dy * k + 2 * r + 2);
  return `<svg class="ib-hexmap" viewBox="0 0 ${w} ${h}" width="${w}" height="${h}" aria-hidden="true">${out}</svg>`;
}

// ---------------------------------------------------------------------------
// The spec of each event
// ---------------------------------------------------------------------------

/** What the record writes for one event. A fold row reads the same
 *  functions with `count` ≥ 2 and the merged figures (inbox.md §4). */
interface Spec {
  type(): string;
  subject(f: Facts): string;
  cells(f: Facts): Cell[];
  body(f: Facts): string[];
  table?(f: Facts): OpenTable | null;
  /** The hex map or the figure grid that stands in place of a table. */
  figure?(f: Facts): string;
  place?(f: Facts): Place | null;
}

/** The place of a message with a `place` key or a cell. */
function defaultPlace(f: Facts): Place | null {
  if (f.cell <= 0 && !has(f, 'place')) return null;
  return { name: placeName(f), cell: f.cell };
}

/** The place of an object: the site name and the cell. */
function sitePlace(f: Facts): Place | null {
  const name = str(f, 'site') || placeName(f);
  if (!name && f.cell <= 0) return null;
  return { name, cell: f.cell };
}

/**
 * The after-action line (numbers.md §4.1, R2380): the opening shot of the
 * fight that sank the hull, "Opened from the stern, unheard: 60". The
 * server writes the arc as the HitAspect number; a message with no arc
 * (the flag off, or a fight opened by no gun) states no line.
 */
function openingLine(f: Facts): string | null {
  const aspect = ['', S.open_bow, S.open_beam, S.open_stern][int(f, 'open_aspect')] ?? '';
  if (!aspect) return null;
  const words = { aspect, damage: fig(int(f, 'open_damage')) };
  return has(f, 'open_ambush') ? S.open_ambush(words) : S.open_heard(words);
}

const LOSS: Spec = {
  type: () => S.type_fleet_lost,
  subject: (f) => str(f, 'fleet'),
  cells: (f) => {
    const fate = str(f, 'fate');
    const killer = has(f, 'killer') || int(f, 'killer_faction') > 0
      ? fac(0, int(f, 'killer_faction'), str(f, 'killer'))
      : txt(0, '');
    return [
      txt(10, className(f.s, str(f, 'class')), 'dim'),
      txt(10, fate === 'captured' ? S.fate_captured : fate === 'wounded' ? S.fate_wounded : '', 'bad'),
      killer,
    ];
  },
  body: (f) => {
    const out: string[] = [];
    const named = { fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))) };
    out.push(has(f, 'killer')
      ? S.lost_by({ ...named, killer: esc(str(f, 'killer')), killer_owner: esc(str(f, 'killer_owner')) })
      : S.lost_sunk(named));
    const fate = str(f, 'fate');
    if (fate === 'captured') {
      out.push(withResIcons(S.lost_captured({
        captain: esc(str(f, 'captain')), captor_faction: factionHtml(int(f, 'captor_faction')),
        ransom: fig(int(f, 'ransom')),
      })));
    } else if (fate === 'wounded') {
      out.push(S.lost_wounded({ captain: esc(str(f, 'captain')), sickbay_until: timeOf(f, 'sickbay_until') }));
    } else if (fate === 'none') {
      out.push(S.lost_none);
    }
    if (has(f, 'cargo')) out.push(S.lost_cargo({ cargo: fig(int(f, 'cargo')) }));
    const opening = openingLine(f);
    if (opening) out.push(opening);
    return out;
  },
  table: (f) => {
    const rows = table(f, 'damage');
    if (rows.length === 0) return null;
    return {
      title: S.damage_title, heads: S.damage_cols, num: [false, false, false, true],
      rows: rows.map((r) => [
        sourceCell(r), classCell(f, r), ownerCell(r), tableFig(cellInt(r, 'damage')),
      ]),
    };
  },
  place: defaultPlace,
};

const ROOM_MADE: Spec = {
  type: () => S.type_room_made,
  subject: (f) => str(f, 'fleet'),
  cells: (f) => [
    txt(10, className(f.s, str(f, 'class')), 'dim'),
    txt(0, S.for_class({ class: className(f.s, str(f, 'for_class')) })),
  ],
  body: (f) => [S.room_body({
    fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))),
    for_class: esc(className(f.s, str(f, 'for_class'))),
  })],
};

const CARGO_SPILLED: Spec = {
  type: () => S.type_cargo_spilled,
  subject: () => S.storage_full,
  cells: (f) => [
    num(7, int(f, 'units'), S.landed({ n: int(f, 'units') })),
    num(7, int(f, 'spilled'), S.spilled({ n: int(f, 'spilled') }), { ink: 'bad' }),
  ],
  body: (f) => [S.spilled_body({ units: fig(int(f, 'units')), spilled: fig(int(f, 'spilled')) })],
};

const FLEET_KILLED: Spec = {
  type: () => S.type_fleet_killed,
  subject: (f) => (f.count ? placeName(f) : str(f, 'fleet')),
  // A row states the class and the sinkings. The share and the salvage
  // stand in the open message only.
  cells: (f) => [
    txt(10, f.count ? '' : className(f.s, str(f, 'class')), 'dim'),
    f.count ? txt(14, S.sunk({ n: fig(f.count) })) : facCell(f, 14, 'faction', 'owner'),
  ],
  body: (f) => {
    const out = [S.killed_body({
      fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))),
      owner: esc(str(f, 'owner')), faction: factionHtml(int(f, 'faction')),
    })];
    out.push(S.killed_share({ share: int(f, 'share'), damage: fig(int(f, 'damage')) }));
    if (has(f, 'prize_alloy') || has(f, 'prize_biomass') || has(f, 'prize_volatiles')) {
      out.push(S.killed_salvage({
        alloy: fig(int(f, 'prize_alloy')), biomass: fig(int(f, 'prize_biomass')),
        volatiles: fig(int(f, 'prize_volatiles')),
      }));
    }
    // A fold row sums the sinkings of one place; the opening shot is the
    // fact of one fight, so it stands in a single message alone.
    const opening = f.count ? null : openingLine(f);
    if (opening) out.push(opening);
    return out;
  },
  table: (f) => {
    const rows = table(f, 'dealt');
    if (rows.length === 0) return null;
    return {
      heads: S.dealt_cols, num: [false, false, true],
      rows: rows.map((r) => [fleetCell(r), classCell(f, r), tableFig(cellInt(r, 'damage'))]),
    };
  },
  place: defaultPlace,
};

/** The launcher of a round in a sentence, by the key `source`. */
function launcher(f: Facts): string {
  switch (int(f, 'source')) {
    case 1: return S.launcher_tube;
    case 2: return S.launcher_hold({ owner: esc(str(f, 'owner') || (f.s.hello?.playerName ?? '')) });
    case 3: return S.launcher_battery;
  }
  return esc(str(f, 'launcher'));
}

/** The two ends of a torpedo: `struck` is the report of the reader's hulls. */
function torpedoSpec(struck: boolean): Spec {
  return {
    type: () => (struck ? S.type_torpedo_struck : S.type_torpedo_hit),
    subject: (f) => placeName(f),
    cells: (f) => [
      num(7, int(f, 'count'), S.hits({ n: int(f, 'count') })),
      num(7, int(f, 'sunk'), S.sunk({ n: int(f, 'sunk') }), { ink: struck ? 'bad' : 'good' }),
      facCell(f, 0, 'faction'),
    ],
    body: (f) => {
      const base = { torpedo: esc(torpedoName(f.s, str(f, 'torpedo'))), launcher: launcher(f), count: fig(int(f, 'count')) };
      const out = [struck
        ? S.torpedo_struck_body({ ...base, owner: esc(str(f, 'owner')) })
        : S.torpedo_hit_body({ ...base, faction: factionHtml(int(f, 'faction')) })];
      if (int(f, 'sunk') > 0) out.push(S.torpedo_sank({ sunk: fig(int(f, 'sunk')) }));
      return out;
    },
    table: (f) => {
      const rows = table(f, 'hits');
      if (rows.length === 0) return null;
      return struck
        ? {
          heads: S.struck_cols, num: [false, false, true, true],
          rows: rows.map((r) => [fleetCell(r), classCell(f, r),
            tableFig(cellInt(r, 'damage')), hullCell(r)]),
        }
        : {
          heads: S.hits_cols, num: [false, false, false, true, true],
          rows: rows.map((r) => [fleetCell(r), classCell(f, r), ownerCell(r),
            tableFig(cellInt(r, 'damage')), hullCell(r)]),
        };
    },
    place: defaultPlace,
  };
}

/** The outcome word of a battle report, its ink and its sentence, from
 *  the reader's side (R1835). */
function outcome(f: Facts): { word: string; ink: Ink; say: string } {
  const site = esc(str(f, 'site'));
  switch (str(f, 'outcome')) {
    case 'taken': return { word: S.outcome_taken, ink: 'good', say: S.battle_taken({ site }) };
    case 'won': return { word: S.outcome_won, ink: 'good', say: S.battle_won({ site }) };
    case 'failed': return { word: S.outcome_failed, ink: 'bad', say: S.battle_failed({ site }) };
    case 'held': return { word: S.outcome_held, ink: 'good', say: S.battle_held({ site }) };
    case 'gauge_lost': return { word: S.outcome_gauge_lost, ink: 'bad', say: S.battle_gauge_lost({ site }) };
    case 'lost': return {
      word: S.outcome_lost, ink: 'bad', say: S.battle_lost({ site, foe: factionHtml(int(f, 'foe')) }),
    };
  }
  return { word: '', ink: '', say: '' };
}

/** Is a battle report the copy a clanmate of the fighters reads? A
 *  fighter's report carries `fought` (inbox.md §2, R1854). */
export function isClanCopy(f: Facts): boolean {
  return f.ev === 'battle_report' && !has(f, 'fought');
}

const BATTLE: Spec = {
  type: () => S.type_battle_report,
  subject: (f) => str(f, 'site') || placeName(f),
  cells: (f) => {
    const o = outcome(f);
    return [
      txt(12, o.word, o.ink),
      txt(7, S.score({ ours: int(f, 'ours'), theirs: int(f, 'theirs') }), '', true),
      // A clanmate's copy carries the CLAN mark in a fixed cell; a
      // fighter's report keeps the cell empty (inbox.md §2, R1854).
      txt(6, isClanCopy(f) ? S.clan_copy : '', 'dim'),
    ];
  },
  body: (f) => {
    const out: string[] = [];
    if (isClanCopy(f)) out.push(S.battle_copy);
    const o = outcome(f);
    if (o.say) out.push(o.say);
    out.push(S.battle_foe({ foe: factionHtml(int(f, 'foe')), ours: int(f, 'ours'), theirs: int(f, 'theirs') }));
    out.push(S.battle_damage({ dealt: fig(int(f, 'dealt')), taken: fig(int(f, 'taken')), gauge: esc(str(f, 'gauge') || '0') }));
    return out;
  },
  table: (f) => {
    const rows = table(f, 'fleets');
    if (rows.length === 0) return null;
    return {
      heads: S.fleets_cols, num: [false, false, false, false, true, true, false],
      rows: rows.map((r) => [
        esc(r.cells.side === 'defence' ? S.side_defence : S.side_attack),
        fleetCell(r), classCell(f, r), ownerCell(r),
        tableFig(cellInt(r, 'dealt')), tableFig(cellInt(r, 'taken')),
        (r.cells.withdrew ?? '') !== '' ? esc(S.yes) : DASH,
      ]),
    };
  },
  place: sitePlace,
};

const CAPTAIN_TAKEN: Spec = {
  type: () => S.type_captain_taken,
  subject: (f) => str(f, 'captain'),
  cells: (f) => [
    facCell(f, 14, 'faction', 'owner'),
    num(8, int(f, 'ransom'), S.credits({ n: int(f, 'ransom') }), { ink: 'good' }),
  ],
  body: (f) => [withResIcons(S.taken_body({
    captain: esc(str(f, 'captain')), owner: esc(str(f, 'owner')), ransom: fig(int(f, 'ransom')),
  }))],
};

const FIRE_NEAR: Spec = {
  type: () => S.type_hold_under_fire,
  subject: () => S.your_hold,
  cells: (f) => [
    facCell(f, 14, 'faction'),
    num(9, int(f, 'events'), S.shots({ n: int(f, 'events') })),
    txt(13, has(f, 'first') ? range(int(f, 'first'), int(f, 'last') || int(f, 'first'), f.now) : ''),
  ],
  body: (f) => [S.fire_body({
    faction: factionHtml(int(f, 'faction')), events: fig(int(f, 'events')),
    first: timeOf(f, 'first'), last: timeOf(f, 'last'),
  })],
  place: (f) => (f.cell > 0 ? { name: S.your_hold, cell: f.cell } : null),
};

const WATER_LOST: Spec = {
  type: () => S.type_territory_lost,
  subject: (f) => placeName(f),
  cells: (f) => [
    num(9, int(f, 'cells'), S.cells_lost({ n: int(f, 'cells') }), { ink: 'bad' }),
    facCell(f, 0, 'faction'),
  ],
  body: (f) => [S.water_body({ faction: factionHtml(int(f, 'faction')), cells: fig(int(f, 'cells')) })],
  figure: (f) => hexMap(table(f, 'cells').map((r) => cellInt(r, 'cell')).filter((c) => c > 0)),
  place: defaultPlace,
};

/** The state of a siege window: the time to it, OPEN, or ENDED dim
 *  (audit P1). The countdown counts minutes (audit P11). */
function windowState(f: Facts): Cell {
  const start = int(f, 'window_start');
  const end = int(f, 'window_end');
  if (end > 0 && f.now >= end) return txt(9, S.window_ended, 'dim', true);
  if (start > 0 && f.now >= start) return txt(9, S.window_open, 'bad', true);
  if (start > 0) return txt(9, S.window_in({ span: span(start - f.now) }), '', true);
  return txt(9, '', '', true);
}

const SIEGE: Spec = {
  type: () => S.type_siege_defend,
  subject: (f) => str(f, 'site') || placeName(f),
  cells: (f) => [
    facCell(f, 14, 'foe'),
    txt(13, has(f, 'window_start') ? range(int(f, 'window_start'), int(f, 'window_end'), f.now) : ''),
    windowState(f),
  ],
  body: (f) => [S.siege_body({
    foe: factionHtml(int(f, 'foe')), site: esc(str(f, 'site')),
    window_start: timeOf(f, 'window_start'), window_end: timeOf(f, 'window_end'),
  })],
  place: sitePlace,
};

const HOLD_MOVED: Spec = {
  type: () => S.type_hold_moved,
  subject: (f) => cellLabel(int(f, 'to_cell')),
  cells: (f) => [txt(10, str(f, 'cause') === 'seaquake' ? S.cause_seaquake : '', 'dim')],
  body: (f) => [S.moved_body({ to_cell: esc(cellLabel(int(f, 'to_cell'))) })],
  place: (f) => {
    const cell = int(f, 'to_cell') || f.cell;
    return cell > 0 ? { name: cellLabel(cell), cell } : null;
  },
};

/** The three window results of an object of the side (inbox.md §2). */
type ObjectResult = 'fallen' | 'mauled' | 'held';

/** The cause and the cost of a window result (R3275): the damage of each
 *  side, what the object gives (the benefit a fall took away) and when
 *  the next window opens. A message filed before R3275 has none of the
 *  keys, and states its first sentence alone. */
function objectCost(f: Facts, result: ObjectResult): string[] {
  const out: string[] = [];
  if (has(f, 'atk_damage') || has(f, 'def_damage')) {
    out.push(S.object_damage_body({ dealt: fig(int(f, 'def_damage')), taken: fig(int(f, 'atk_damage')) }));
  }
  const gain = has(f, 'kind')
    ? benefitText(int(f, 'kind'), int(f, 'benefit_milli') / 1000, int(f, 'benefit2_milli') / 1000) : '';
  if (gain && result === 'fallen') out.push(S.object_lost_body({ gain: esc(gain) }));
  if (result === 'mauled') out.push(S.object_heal_body);
  if (has(f, 'next_window')) out.push(S.object_next_body({ at: timeOf(f, 'next_window') }));
  return out;
}

function objectSpec(result: ObjectResult): Spec {
  return {
    type: () => (result === 'held' ? S.type_object_held
      : result === 'mauled' ? S.type_object_mauled : S.type_object_fallen),
    subject: (f) => str(f, 'site') || placeName(f),
    cells: (f) => [facCell(f, 14, 'foe')],
    body: (f) => {
      const named = { site: esc(str(f, 'site')), foe: factionHtml(int(f, 'foe')) };
      const head = result === 'held' ? S.held_body(named)
        : result === 'mauled' ? S.mauled_body(named) : S.fell_body(named);
      return [head, ...objectCost(f, result)];
    },
    place: sitePlace,
  };
}

/** The featured object of the next world day (R3272): the window of that
 *  day and the two doubles. */
const FEATURED: Spec = {
  type: () => S.type_featured_next,
  subject: (f) => str(f, 'site') || placeName(f),
  cells: (f) => [
    txt(13, has(f, 'window_start') ? range(int(f, 'window_start'), int(f, 'window_end'), f.now) : ''),
    windowState(f),
  ],
  body: (f) => [
    S.featured_body({
      site: esc(str(f, 'site')), window_start: timeOf(f, 'window_start'), window_end: timeOf(f, 'window_end'),
    }),
    S.featured_rule,
  ],
  place: sitePlace,
};

/** Did a dive bring home a core? The event id says it when the key is absent. */
const diveCore = (f: Facts): boolean =>
  has(f, 'core') || f.ev === 'expedition_core' || f.ev === 'expedition_hurt_core';

/** Did a dive hurt its captain? The event id says it when the key is absent. */
const diveHurt = (f: Facts): boolean =>
  has(f, 'hurt') || f.ev === 'expedition_hurt' || f.ev === 'expedition_hurt_core';

/** A dive of the four outcomes: one shape (089 · 10). */
const DIVE: Spec = {
  type: () => S.type_expedition,
  subject: (f) => str(f, 'captain'),
  cells: (f) => {
    const until = int(f, 'sickbay_until');
    return [
      txt(8, S.depth_cells[int(f, 'depth') - 1] ?? '', 'dim'),
      num(6, int(f, 'isotopes'), String(int(f, 'isotopes')), { mark: { icon: 'isotopes', res: true } }),
      { t: 'flag', on: diveCore(f), mark: { icon: 'core', res: true } },
      { t: 'flag', on: diveHurt(f), mark: { icon: 'hurt' }, ink: 'bad' },
      txt(6, until > 0 ? shortStamp(until, f.now) : '', '', true),
    ];
  },
  body: (f) => {
    const captain = esc(str(f, 'captain'));
    const dive = diveSpec(f.s, int(f, 'depth'))?.name ?? '';
    const out = [S.dive_body({ dive: esc(dive), captain, hours: fig(int(f, 'hours')) })];
    const isotopes = fig(int(f, 'isotopes'));
    out.push(diveCore(f) ? S.dive_isotopes_core({ isotopes }) : S.dive_isotopes({ isotopes }));
    if (diveHurt(f) && has(f, 'sickbay_until')) out.push(S.dive_hurt({ captain, sickbay_until: timeOf(f, 'sickbay_until') }));
    if (int(f, 'years') > 0) out.push(S.dive_years({ captain, n: int(f, 'years') }));
    return out;
  },
};

const SALE_ISOTOPES: Spec = {
  type: () => S.type_sale,
  subject: () => S.isotopes,
  cells: (f) => {
    const qty = int(f, 'qty');
    return [
      num(8, int(f, 'price'), S.credits({ n: int(f, 'price') }), { ink: 'good' }),
      num(6, qty, String(qty), { mark: { icon: 'isotopes', res: true } }),
      f.count ? txt(0, S.buyers({ n: f.count })) : facCell(f, 0, 'buyer_faction', 'buyer'),
    ];
  },
  body: (f) => [withResIcons(S.sale_isotopes_body({
    buyer: esc(str(f, 'buyer')), qty: fig(int(f, 'qty')), price: fig(int(f, 'price')), unit: fig(int(f, 'unit')),
  }))],
};

const SALE_PRINT: Spec = {
  type: () => S.type_sale,
  subject: (f) => str(f, 'print'),
  cells: (f) => [
    num(8, int(f, 'price'), S.credits({ n: int(f, 'price') }), { ink: 'good' }),
    txt(6, f.count ? S.prints({ n: f.count }) : S.print, 'dim'),
    txt(6, ''),
    facCell(f, 0, 'buyer_faction', 'buyer'),
  ],
  body: (f) => [withResIcons(S.sale_print_body({
    buyer: esc(str(f, 'buyer')), print: esc(str(f, 'print')), price: fig(int(f, 'price')),
  }))],
};

/** The club share of the casino (casino.md §16, R2144): the room of the
 *  club (R2165), the share, the window of the light and the split. The
 *  INBOX folds the payouts of one world day (inbox.md §4): a fold row
 *  counts them and states the summed money, and it reads no window fact,
 *  because the merge keeps only the newest one. Each member line keeps
 *  its own room and share. */
const CLUB_PAID: Spec = {
  type: () => S.type_club_paid,
  subject: (f) => (f.count ? S.club_shares({ n: f.count }) : roomName(int(f, 'faction'))),
  cells: (f) => [
    num(8, int(f, 'amount'), S.credits({ n: int(f, 'amount') }), { ink: 'good' }),
    txt(6, f.count ? '' : str(f, 'window') === 'night' ? S.club_night : S.club_day, 'dim'),
    txt(0, f.count ? '' : S.club_split({ total: fig(int(f, 'total')), members: int(f, 'members') }), 'dim'),
  ],
  body: (f) => {
    if (f.count) {
      return [S.club_fold_body({ n: f.count, amount: fig(int(f, 'amount')), net: fig(int(f, 'net')) })];
    }
    const out = [
      S.club_body({
        room: esc(roomName(int(f, 'faction'))), faction: factionHtml(int(f, 'faction')), net: fig(int(f, 'net')),
        window: str(f, 'window') === 'night' ? S.club_in_night : S.club_in_day,
      }),
      S.club_share_body({ amount: fig(int(f, 'amount')), total: fig(int(f, 'total')), members: int(f, 'members') }),
    ];
    if (has(f, 'from')) {
      out.push(S.club_window_body({ from: timeOf(f, 'from'), to: timeOf(f, 'to') }));
    }
    return out;
  },
};

const SEASON: Spec = {
  type: () => S.type_season_closed,
  subject: (f) => S.season({ season: int(f, 'season') }),
  cells: (f) => [
    txt(10, S.rank({ rank: int(f, 'rank'), of: int(f, 'of') })),
    num(8, int(f, 'points'), S.points({ n: int(f, 'points') })),
    num(7, int(f, 'honour'), S.honour({ n: int(f, 'honour') }), { mark: { icon: 'honour' }, ink: 'good' }),
    txt(0, str(f, 'title')),
  ],
  body: (f) => [
    S.season_body({ season: int(f, 'season'), rank: int(f, 'rank'), of: int(f, 'of'), points: fig(int(f, 'points')) }),
    S.season_honour({ honour: fig(int(f, 'honour')), title: esc(str(f, 'title')) }),
  ],
  // The scorecard of 089 · 12 a: the season figures, each over its word.
  // A zero reads as a faint dash: no prisoner freed reads – (audit D4).
  figure: (f) => '<div class="ib-score">' + (['cells', 'kills', 'sieges', 'dives', 'freed'] as const)
    .map((k, i) => `<span><b>${tableFig(int(f, k))}</b><i>${esc(S.season_figures[i] ?? '')}</i></span>`)
    .join('') + '</div>',
};

const SQUAD: Spec = {
  type: () => S.type_pickup_squad,
  subject: () => S.pickup_squad,
  cells: (f) => {
    const n = table(f, 'mates').length || int(f, 'mates');
    return [num(10, n, S.mates({ n }))];
  },
  body: () => [S.squad_body],
  table: (f) => {
    const rows = table(f, 'mates');
    if (rows.length === 0) return null;
    return {
      heads: S.mates_cols, num: [false, false],
      rows: rows.map((r) => [esc(r.cells.name ?? ''), factionHtml(cellInt(r, 'faction'))]),
    };
  },
};

/** The name of the achievement of one row. */
function achievementOf(f: Facts): string {
  return achievementName(str(f, 'achievement'));
}

/** The grade word of one row, or '' for a recognition (no grade). */
function gradeOf(f: Facts): string {
  return has(f, 'zone') ? gradeName(int(f, 'zone')) : '';
}

/** The achievements of a fold: the first three names, then +n. */
export function achievementNames(names: readonly string[]): string {
  const head = names.slice(0, 3).join(', ');
  return names.length > 3 ? `${head} ${S.more({ n: names.length - 3 })}` : head;
}

const ACHIEVEMENT: Spec = {
  type: () => S.type_achievement,
  subject: (f) => (f.count ? S.achieved({ n: f.count }) : achievementOf(f)),
  cells: (f) => [f.count
    ? txt(0, achievementNames(f.members.map((m) => achievementName(m.params.achievement ?? ''))), 'dim')
    : txt(8, gradeOf(f), 'dim')],
  body: (f) => {
    const grade = gradeOf(f);
    return grade ? [S.achievement_body({ grade: esc(grade), achievement: esc(achievementOf(f)) })] : [];
  },
};

/** A roll of a redraft, from its thousandths on the wire (inbox.md §3):
 *  two digits, the form of the grade odds of the build card. */
function rollFig(f: Facts, k: string): string {
  return (int(f, k) / 1000).toFixed(2);
}

/** The module of a redraft's slot: the catalog name of its family and
 *  the numeral of its tier. */
function redraftModule(f: Facts): string {
  const family = int(f, 'family');
  const tier = int(f, 'tier');
  const spec = f.s.catalog?.modules.find((m) => m.family === family && m.tier === tier);
  return S.redraft_module({ module: spec?.name ?? '', tier: S.redraft_tiers[tier - 1] ?? '' });
}

/** A grade code as its word, in brackets in a sentence; '' reads as a
 *  dash, because a roll below every band has no grade. */
function gradeWord(f: Facts, k: string): string {
  return has(f, k) ? moduleGradeWord(str(f, k)) : '–';
}

/** A redraft (R1884): the master improved in place, or the redraft was
 *  discarded. One shape for both outcomes. */
const REDRAFT: Spec = {
  type: () => S.type_redraft_done,
  subject: (f) => str(f, 'design') || redraftModule(f),
  cells: (f) => {
    const improved = str(f, 'outcome') === 'improved';
    return [
      txt(10, redraftModule(f), 'dim'),
      txt(14, S.redraft_rolls({ old: rollFig(f, 'old_roll'), new: rollFig(f, 'new_roll') })),
      txt(0, improved ? S.redraft_improved : S.redraft_discarded, improved ? 'good' : 'dim'),
    ];
  },
  body: (f) => [
    S.redraft_body({ slot: int(f, 'slot'), design: esc(str(f, 'design')), module: esc(redraftModule(f)) }),
    S.redraft_roll_body({
      old: rollFig(f, 'old_roll'), old_grade: esc(gradeWord(f, 'old_grade')),
      new: rollFig(f, 'new_roll'), new_grade: esc(gradeWord(f, 'new_grade')),
    }),
    str(f, 'outcome') === 'improved' ? S.redraft_improved_body : S.redraft_discarded_body,
  ],
};

/**
 * A hull of the NPC side hunts a hull of the reader (v2 §8, R2637): a
 * pirate group (`pirate_contact`) or a monster (`monster_heard`). The row
 * names the hunted hull and its class, and a fold the count of contacts
 * of the one group. The place is the cell of the hull at the contact.
 */
function npcContactSpec(monster: boolean): Spec {
  return {
    type: () => (monster ? S.type_monster_heard : S.type_pirate_contact),
    subject: (f) => str(f, 'fleet'),
    cells: (f) => [
      txt(10, className(f.s, str(f, 'class')), 'dim'),
      f.count > 1 ? num(12, f.count, S.npc_contacts({ n: f.count }), { ink: 'bad' }) : txt(12, ''),
    ],
    body: (f) => {
      const named = { fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))) };
      return [monster ? S.monster_body(named) : S.pirate_body(named)];
    },
    place: defaultPlace,
  };
}

/**
 * The last hit of the reader sank a pirate hull and found the chart
 * fragment of its den (v2 §8.2, R2637). The row names the sunk hull and
 * the count of fragments the reader holds, from `Empire.fragments`; the
 * place is the wreck. The pirate cache itself is phase 5 (§9).
 */
const FRAGMENT: Spec = {
  type: () => S.type_fragment_found,
  subject: (f) => str(f, 'fleet'),
  cells: (f) => {
    const held = f.s.empire?.fragments.length ?? 0;
    return [
      txt(10, className(f.s, str(f, 'class')), 'dim'),
      held > 0 ? num(9, held, S.fragments_held({ n: held }), { ink: 'good' }) : txt(9, ''),
    ];
  },
  body: (f) => {
    const out = [S.fragment_body({ fleet: esc(str(f, 'fleet')) }), S.fragment_note];
    const held = f.s.empire?.fragments.length ?? 0;
    if (held > 0) out.push(S.fragments_body({ n: held }));
    return out;
  },
  place: defaultPlace,
};

/**
 * A monster sank, and the fleets of the reader fired on it (R2734). The
 * row names the monster, its class and the count of commanders the kill
 * credits; the open message gives the share of the reader, the last hit,
 * the opening shot and the `dealt` table, as an enemy sunk does.
 */
const MONSTER_SUNK: Spec = {
  type: () => S.type_monster_sunk,
  subject: (f) => str(f, 'fleet'),
  cells: (f) => [
    txt(10, className(f.s, str(f, 'class')), 'dim'),
    num(14, int(f, 'commanders'), S.monster_commanders({ n: int(f, 'commanders') }), { ink: 'good' }),
  ],
  body: (f) => {
    const out = [S.monster_sunk_body({ fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))) })];
    if (has(f, 'share')) out.push(S.killed_share({ share: int(f, 'share'), damage: fig(int(f, 'damage')) }));
    if (str(f, 'last') === '1') out.push(S.monster_sunk_last);
    // The monster reward (v2 §8.7, R2762): the facts state what the
    // stores of the reader took, the Data Core and the practice points.
    if ((['alloy', 'biomass', 'volatiles', 'isotopes'] as const).some((k) => int(f, k) > 0)) out.push(rewardLine(f));
    if (has(f, 'core')) out.push(S.monster_sunk_core);
    if (int(f, 'practice') > 0) out.push(S.monster_sunk_practice({ points: practiceFig(int(f, 'practice') / 100) }));
    const opening = openingLine(f);
    if (opening) out.push(opening);
    return out;
  },
  table: (f) => {
    const rows = table(f, 'dealt');
    if (rows.length === 0) return null;
    return {
      heads: S.dealt_cols, num: [false, false, true],
      rows: rows.map((r) => [fleetCell(r), classCell(f, r), tableFig(cellInt(r, 'damage'))]),
    };
  },
  place: defaultPlace,
};

/**
 * The boarding of a support hull of the reader (v2 §8.6, R2721, round
 * 111 card 01): pirates began to board it (`boarding_started`), took it
 * (`hull_taken`), or a combat hull of the reader stopped the boarding
 * (`boarding_stopped`). The row names the hull and its class; the place
 * is the cell of the boarding.
 */
function boardingSpec(ev: 'started' | 'taken' | 'stopped'): Spec {
  // The fact `class` is a class number, or the class word the hub wrote in
  // the language of the reader (eventcfg registry): both read.
  const cls = (f: Facts) => {
    const v = str(f, 'class');
    return /^\d+$/.test(v) ? className(f.s, v) : v;
  };
  return {
    type: () => (ev === 'started' ? S.type_boarding_started : ev === 'taken' ? S.type_hull_taken : S.type_boarding_stopped),
    subject: (f) => str(f, 'fleet'),
    cells: (f) => [txt(10, cls(f), 'dim')],
    body: (f) => {
      const named = { fleet: esc(str(f, 'fleet')), class: esc(cls(f)) };
      if (ev === 'started') return [S.boarding_body(named)];
      if (ev === 'stopped') return [S.freed_body(named)];
      return [S.hull_taken_body(named), S.hull_taken_note];
    },
    place: defaultPlace,
  };
}

/** The word of a cache kind of the fact `kind` (v2 §9.1, R2643): the
 *  word of the cache sheet, so a kind reads one way everywhere. */
function cacheKindWord(kind: string): string {
  switch (kind) {
    case 'seeded': return CS.cache_kind_seeded;
    case 'ruin': return CS.cache_kind_ruin;
    case 'pirate': return CS.cache_kind_pirate;
    case 'player': return CS.cache_kind_player;
    case 'event': return CS.cache_kind_event;
  }
  return CS.cache_kind_other;
}

/** The four resources a find or a recovery landed, as four cells of one
 *  shape: a zero reads as a faint dash. */
function rewardCells(f: Facts): Cell[] {
  return (['alloy', 'biomass', 'volatiles', 'isotopes'] as const).map((k) =>
    num(6, int(f, k), String(int(f, k)), { mark: { icon: k, res: true } }));
}

/** The sentence of what the stores took. */
function rewardLine(f: Facts): string {
  return S.cache_found_reward({
    alloy: fig(int(f, 'alloy')), biomass: fig(int(f, 'biomass')),
    volatiles: fig(int(f, 'volatiles')), isotopes: fig(int(f, 'isotopes')),
  });
}

/**
 * A hull of the reader found a cache (v2 §9.1, §9.3, R2643, R2649). Line 1
 * names the cache by the label of an event cache or its kind; line 2 the
 * hull, the four resources the stores took and the Data Core of a ruin.
 * The note of a player cache stands on the find card alone, because only
 * the finder reads it.
 */
const CACHE_FOUND: Spec = {
  type: () => S.type_cache_found,
  subject: (f) => str(f, 'label') || cacheKindWord(str(f, 'kind')),
  cells: (f) => [
    txt(10, str(f, 'fleet'), 'dim'),
    ...rewardCells(f),
    { t: 'flag', on: has(f, 'core'), mark: { icon: 'core', res: true } },
  ],
  body: (f) => {
    const kind = str(f, 'kind');
    const out = [S.cache_found_body({ fleet: esc(str(f, 'fleet')), kind: esc(cacheKindWord(kind)) })];
    out.push(has(f, 'core') ? S.cache_found_core : rewardLine(f));
    if (kind === 'player') out.push(S.cache_found_player);
    if (kind === 'event' && has(f, 'label')) out.push(S.cache_found_event({ label: esc(str(f, 'label')) }));
    return out;
  },
  place: defaultPlace,
};

/**
 * Another commander found a player cache of the reader (v2 §9.3, R2643,
 * R2649): the depositor's fact. Line 2 names the place and the finders of
 * the cache this season.
 */
const CACHE_TAKEN: Spec = {
  type: () => S.type_cache_taken,
  subject: (f) => str(f, 'finder'),
  cells: (f) => [
    txt(10, cellLabel(f.cell), 'dim'),
    num(9, int(f, 'finds'), S.cache_finders({ n: int(f, 'finds') }), { ink: 'good' }),
  ],
  body: (f) => [
    S.cache_taken_body({ finder: esc(str(f, 'finder')) }),
    S.cache_finders_body({ n: int(f, 'finds') }),
  ],
  place: defaultPlace,
};

/** A SEARCH took a share of the cargo of a wreck (v2 §9.2, R2643, R2860,
 *  R2649): the hull, the class of the sunk hull and what the stores took. */
const WRECK_RECOVERED: Spec = {
  type: () => S.type_wreck_recovered,
  subject: (f) => str(f, 'fleet'),
  cells: (f) => [txt(10, className(f.s, str(f, 'class')), 'dim'), ...rewardCells(f)],
  body: (f) => [
    S.recovered_body({ fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))) }),
    rewardLine(f),
  ],
  place: defaultPlace,
};

/** A sea object by its `SeaObjectKind` number, named from the hello
 *  catalog. */
function seaObjectName(s: GameState, v: string): string {
  return s.seaObject(Number(v))?.name ?? '';
}

/**
 * A sea mine went off on a hull (v2 §14.7 "Events", R2887): the struck
 * commander reads `sea_mine_struck`, the layer `sea_mine_tripped`. The
 * row names the struck hull or the kind, the struck hulls and the hulls
 * that sank, and the other side. A fold of the layer sums the hulls and
 * the damage of its window.
 */
function seaMineSpec(tripped: boolean): Spec {
  return {
    type: () => (tripped ? S.type_sea_mine_tripped : S.type_sea_mine_struck),
    subject: (f) => (tripped ? seaObjectName(f.s, str(f, 'kind')) : str(f, 'fleet')),
    cells: (f) => [
      tripped
        ? num(7, int(f, 'count'), S.hits({ n: int(f, 'count') }), { ink: 'good' })
        : txt(10, className(f.s, str(f, 'class')), 'dim'),
      num(7, int(f, 'sunk'), S.sunk({ n: int(f, 'sunk') }), { ink: tripped ? 'good' : 'bad' }),
      facCell(f, 0, 'faction', 'owner'),
    ],
    body: (f) => {
      const named = {
        fleet: esc(str(f, 'fleet')), class: esc(className(f.s, str(f, 'class'))),
        owner: esc(str(f, 'owner')), kind: esc(seaObjectName(f.s, str(f, 'kind'))),
      };
      const out = [tripped ? S.sea_mine_tripped_body(named) : S.sea_mine_struck_body(named)];
      if (tripped && int(f, 'count') > 1) {
        out.push(S.sea_mine_hulls({ count: fig(int(f, 'count')), damage: fig(int(f, 'damage')) }));
        if (int(f, 'sunk') > 0) out.push(S.torpedo_sank({ sunk: fig(int(f, 'sunk')) }));
        return out;
      }
      out.push(S.sea_mine_damage({ damage: fig(int(f, 'damage')) }));
      if (int(f, 'sunk') > 0) out.push(S.sea_mine_sank);
      return out;
    },
    place: defaultPlace,
  };
}

/** The word of a reason of `sea_object_lost` (R2887). */
function seaReasonWord(reason: string): string {
  switch (reason) {
    case 'expired': return S.sea_reason_expired;
    case 'destroyed': return S.sea_reason_destroyed;
    case 'cleared': return S.sea_reason_cleared;
    case 'rock': return S.sea_reason_rock;
  }
  return '';
}

/** Who ended the sea objects: the hull and its commander, the commander
 *  alone (a hold tube), or the faction. */
function seaActor(f: Facts): string {
  const fleet = esc(str(f, 'fleet'));
  const owner = esc(str(f, 'owner'));
  if (fleet && owner) return `${fleet} (${owner})`;
  if (fleet || owner) return fleet || owner;
  return int(f, 'faction') > 0 ? factionHtml(int(f, 'faction')) : '';
}

/**
 * Sea objects of the reader left the map without an act of the reader
 * (v2 §14.7 "Events", R2887): their life or their decay ran out, a gun
 * or a round sank them, a Countermine cleared them, or sea mines drifted
 * onto rock. Line 1 names the kind of the last object; line 2 the reason,
 * the count and the other side.
 */
const SEA_OBJECT_LOST: Spec = {
  type: () => S.type_sea_object_lost,
  subject: (f) => seaObjectName(f.s, str(f, 'kind')),
  cells: (f) => [
    txt(10, seaReasonWord(str(f, 'reason')), 'dim'),
    num(7, int(f, 'count'), S.sea_lost({ n: int(f, 'count') }), { ink: 'bad' }),
    facCell(f, 0, 'faction', 'owner'),
  ],
  body: (f) => {
    const count = fig(int(f, 'count'));
    const by = seaActor(f);
    const out: string[] = [];
    switch (str(f, 'reason')) {
      case 'destroyed': out.push(S.sea_lost_destroyed({ by, count })); break;
      case 'cleared': out.push(S.sea_lost_cleared({ by, count })); break;
      case 'rock': out.push(S.sea_lost_rock({ count })); break;
      default: out.push(S.sea_lost_expired({ count }));
    }
    const kind = seaObjectName(f.s, str(f, 'kind'));
    if (kind) out.push(S.sea_lost_kind({ kind: esc(kind) }));
    return out;
  },
  place: defaultPlace,
};

/** A message filed before the build (089 · 13): its stored title on
 *  line 1 and the first 50 characters of its body on line 2. A message
 *  of an event this client does not know reads the same way. */
const LEGACY: Spec = {
  type: () => '',
  subject: (f) => f.title || f.ev,
  cells: (f) => [txt(0, f.body.length > 50 ? `${f.body.slice(0, 50)}…` : f.body, 'dim')],
  body: (f) => (f.body ? [esc(f.body)] : []),
  place: (f) => (f.cell > 0 ? { name: cellLabel(f.cell), cell: f.cell } : null),
};

const SPECS: Readonly<Record<string, Spec>> = {
  fleet_lost: LOSS,
  fleet_lost_cargo: LOSS,
  fleet_lost_by: LOSS,
  room_made: ROOM_MADE,
  cargo_spilled: CARGO_SPILLED,
  fleet_killed: FLEET_KILLED,
  torpedo_hit: torpedoSpec(false),
  torpedo_struck: torpedoSpec(true),
  battle_report: BATTLE,
  captain_taken: CAPTAIN_TAKEN,
  hold_under_fire: FIRE_NEAR,
  territory_lost: WATER_LOST,
  siege_defend: SIEGE,
  hold_moved: HOLD_MOVED,
  object_fallen: objectSpec('fallen'),
  object_mauled: objectSpec('mauled'),
  object_held: objectSpec('held'),
  featured_next: FEATURED,
  expedition_ok: DIVE,
  expedition_core: DIVE,
  expedition_hurt: DIVE,
  expedition_hurt_core: DIVE,
  market_sold_isotopes: SALE_ISOTOPES,
  market_sold_print: SALE_PRINT,
  club_paid: CLUB_PAID,
  season_closed: SEASON,
  pickup_squad: SQUAD,
  achievement: ACHIEVEMENT,
  redraft_done: REDRAFT,
  pirate_contact: npcContactSpec(false),
  monster_heard: npcContactSpec(true),
  fragment_found: FRAGMENT,
  monster_sunk: MONSTER_SUNK,
  boarding_started: boardingSpec('started'),
  hull_taken: boardingSpec('taken'),
  boarding_stopped: boardingSpec('stopped'),
  cache_found: CACHE_FOUND,
  cache_taken: CACHE_TAKEN,
  wreck_recovered: WRECK_RECOVERED,
  sea_mine_struck: seaMineSpec(false),
  sea_mine_tripped: seaMineSpec(true),
  sea_object_lost: SEA_OBJECT_LOST,
};

/** Every event id the record writes words for (plan §3). */
export const RECORD_EVENTS: readonly string[] = Object.keys(SPECS);

/** The spec of an event; the plain row of 089 · 13 for an empty or an
 *  unknown id. */
export function specOf(ev: string): Spec {
  return SPECS[ev] ?? LEGACY;
}

/** The type word of a row; '' for a message filed before the build. */
export function typeWord(f: Facts): string {
  return specOf(f.ev).type();
}

/** Line 1 of a row, as plain text. */
export function subjectOf(f: Facts): string {
  return specOf(f.ev).subject(f);
}

/** The figure cells of line 2. */
export function cellsOf(f: Facts): Cell[] {
  return specOf(f.ev).cells(f);
}

/** The sentences of the open message, as markup. */
export function bodyOf(f: Facts): string[] {
  return specOf(f.ev).body(f);
}

/** The table of the open message, or null. */
export function tableOf(f: Facts): OpenTable | null {
  return specOf(f.ev).table?.(f) ?? null;
}

/** The hex map or the figure grid of the open message, or ''. */
export function figureOf(f: Facts): string {
  return specOf(f.ev).figure?.(f) ?? '';
}

/** The place line of the open message, or null. */
export function placeOf(f: Facts): Place | null {
  return (specOf(f.ev).place ?? defaultPlace)(f);
}

/** A mark as markup, hollow when the slot is empty (build plan §1). */
export function markHtml(mark: Mark, hollow = false): string {
  if (mark.res) {
    const icon = resIcon(mark.icon as ResIcon);
    return hollow ? `<span class="ib-hollow">${icon}</span>` : icon;
  }
  return inboxIcon(mark.icon as InboxIcon, 12, hollow);
}
